export type SalesInsightsSource = 'invoice' | 'order';
export type SalesInsightsPeriod = 'ytd' | 'mtd' | 'qtd' | 'r3' | 'r12' | 'custom';
export type SalesInsightsInterval = 'month' | 'quarter' | 'week' | 'day';
export type SalesInsightsMeasure =
  | 'valueBase'
  | 'valueLocal'
  | 'quantity'
  | 'rowCount'
  | 'customerCount'
  | 'orderCount'
  | 'averageOrderValue';
export type SalesInsightsSectionLayout = 'grid' | 'tabs';

export interface SalesInsightsDashboardSummary {
  id: string;
  name: string;
}

export interface SalesInsightsDashboardDefinition {
  id: string;
  name: string;
  filters: string[];
  sections: SalesInsightsSection[];
  actions?: string[];
}

export interface SalesInsightsSection {
  title?: string;
  layout?: SalesInsightsSectionLayout | null;
  components: SalesInsightsComponentDefinition[];
}

export interface SalesInsightsComponentDefinition {
  type: string;
  title?: string;
  width?: number;
  source?: SalesInsightsSourceDefinition;
  options?: { [key: string]: any };
  error?: string | null;
  accepts?: string[] | null;
}

export interface SalesInsightsSourceDefinition {
  endpoint: string;
  params?: { [key: string]: string | number | boolean };
}

export interface SalesInsightsFilterValues {
  period?: SalesInsightsPeriod;
  customerId?: string[];
  salesperson?: string;
  country?: string;
  dims?: { [name: string]: string };
}

export interface SalesInsightsTotals {
  from?: string;
  to?: string;
  valueBase: number | null;
  valueLocal: number | null;
  currency: string | null;
  quantity: number | null;
  rowCount: number | null;
  customerCount: number | null;
  orderCount?: number | null;
  averageOrderValue?: number | null;
}

export interface SalesInsightsTarget {
  value: number;
  toDate: number;
  attainment: number | null;
}

export interface SalesInsightsTrendPoint {
  key: string;
  valueBase: number | null;
  valueLocal: number | null;
  quantity: number | null;
}

export interface SalesInsightsKpiResponse {
  source: SalesInsightsSource;
  period: SalesInsightsPeriod;
  compare?: string | null;
  computedAt: number;
  current: SalesInsightsTotals;
  comparison: SalesInsightsTotals | null;
  dataFrom?: string | null;
  comparisonComplete?: boolean;
  trend?: SalesInsightsTrendPoint[];
  target?: SalesInsightsTarget | null;
}

export interface SalesInsightsSeriesEntry {
  key: string;
  previousKey?: string;
  current: Partial<SalesInsightsTotals> | null;
  previous: Partial<SalesInsightsTotals> | null;
  target?: number | null;
}

export interface SalesInsightsSeriesResponse {
  entries: SalesInsightsSeriesEntry[];
  computedAt: number;
}

export interface SalesInsightsBreakdownRow {
  key: string;
  name: string | null;
  valueBase: number | null;
  valueLocal: number | null;
  currency: string | null;
  quantity: number | null;
  rowCount: number | null;
  target?: number | null;
  previous?: SalesInsightsBreakdownSums | null;
}

export interface SalesInsightsBreakdownSums {
  valueBase: number | null;
  valueLocal: number | null;
  currency: string | null;
  quantity: number | null;
  rowCount: number | null;
}

export interface SalesInsightsBreakdownResponse {
  rows: SalesInsightsBreakdownRow[];
  computedAt: number;
  compare?: string | null;
  previousFrom?: string | null;
  previousTo?: string | null;
}

export interface SalesInsightsOpenOrderTotals {
  orderCount: number | null;
  valueBase: number | null;
  valueLocal: number | null;
  currency: string | null;
  quantity: number | null;
  rowCount: number | null;
  customerCount: number | null;
}

export interface SalesInsightsOpenOrderRow {
  orderId: string;
  customerId: string;
  customerName: string | null;
  salesperson: string | null;
  salespersonName?: string | null;
  country: string | null;
  currency: string | null;
  orderDate: string | null;
  deliveryDate: string | null;
  valueBase: number | null;
  valueLocal: number | null;
  quantity: number | null;
  rowCount: number | null;
}

export interface SalesInsightsOpenOrdersResponse {
  computedAt: number;
  current: SalesInsightsOpenOrderTotals;
  rows?: SalesInsightsOpenOrderRow[];
}

export interface SalesInsightsExportRequest {
  filters: { [param: string]: string[] };
  titles: string[];
  labels: { [key: string]: string };
}

export interface SalesInsightsStatus {
  computedAt: number | null;
  running: boolean;
  lastError: string | null;
  [key: string]: any;
}

export interface SalesInsightsFilterOption {
  id: string;
  name: string;
}

export const SALES_INSIGHTS_OTHERS_KEY = 'OTHERS';
export const SALES_INSIGHTS_DIM_PREFIX = 'dim.';

export type SalesInsightsCustomerFlag = 'noPurchase3m' | 'noPurchase1m' | 'noContact6m' | 'overdue';

export interface SalesInsightsContact {
  date: string;
  type: string;
}

export interface SalesInsightsCustomerHealthRow {
  customerId: string;
  customerName: string;
  city?: string | null;
  email?: string | null;
  salesperson?: string | null;
  salespersonName?: string | null;
  visitRequired: boolean;
  lastPurchaseDate: string | null;
  daysSinceLastPurchase: number | null;
  invoicedYtd: number | null;
  invoicedPrevYtd: number | null;
  openOrderValue: number | null;
  lastContact: SalesInsightsContact | null;
  nextContact: SalesInsightsContact | null;
  daysSinceLastContact: number | null;
  flags: string[];
  daysSinceLastVisit?: number | null;
}

export interface SalesInsightsCustomerHealthResponse {
  computedAt: number;
  rows: SalesInsightsCustomerHealthRow[];
}

export interface SalesInsightsSegmentCount {
  key: string;
  count: number;
}

export interface SalesInsightsCustomerHealthSummary {
  segments: SalesInsightsSegmentCount[];
  newCustomers: number;
  lostCustomers: number;
}

export interface SalesInsightsActivitiesSummary {
  plannedNext7Days: number;
  plannedByType: { [type: string]: number };
  doneThisMonth: number;
  visitGoalThisMonth: number | null;
  visitsLeadingToOrder: { visits: number; withOrder: number };
  notVisited90Days: number;
}

export type SalesInsightsActivityGroup = 'overdue' | 'today' | 'planned' | 'done';

export const SALES_INSIGHTS_ACTIVITY_GROUPS: SalesInsightsActivityGroup[] = ['overdue', 'today', 'planned', 'done'];

export interface SalesInsightsActivityRow {
  id: string;
  customerId: string;
  customerName: string;
  type: string;
  status: string;
  done: boolean;
  activityDate: string;
  activityEndDate?: string | null;
  customerResponsible?: string | null;
  customerResponsibleName?: string | null;
  comment?: string | null;
  city?: string | null;
}

export interface SalesInsightsActivitiesResponse {
  rows: SalesInsightsActivityRow[];
  groups: Partial<Record<SalesInsightsActivityGroup, number>>;
}

export interface SalesInsightsGoal {
  id: string;
  title: string;
  deadline: string | null;
  total: number;
  done: number;
  remaining: number;
  expired: boolean;
  archived?: boolean;
  scope?: SalesInsightsGoalScope | null;
  startDate?: string | null;
  activityTypes?: string[] | null;
  activityActions?: string[] | null;
  salespersons?: SalesInsightsGoalSalesperson[] | null;
}

export interface SalesInsightsGoalSalesperson {
  code: string | null;
  name: string | null;
  done: number;
  total: number;
}

export interface SalesInsightsGoalScope {
  salesperson?: string | string[] | null;
  country?: string | string[] | null;
  visitRequiredOnly?: boolean;
  dimensions?: { [name: string]: string[] } | null;
  flags?: string[] | null;
  includeCustomerIds?: string[] | null;
  excludeCustomerIds?: string[] | null;
}

export interface SalesInsightsGoalPreview {
  customerCount: number;
  salespersonCount: number;
  salespersons: { code: string | null; name: string | null; customers: number }[];
  customers: SalesInsightsCustomerHealthRow[];
}

export interface SalesInsightsGoalInput {
  title: string;
  deadline: string | null;
  scope: SalesInsightsGoalScope;
  startDate: string | null;
  activityTypes: string[];
  activityActions: string[];
  createActivityType?: boolean;
  newActivityTypeName?: string | null;
}

export type SalesInsightsActivityTypeKind = 'standard' | 'custom' | 'goal';

export const SALES_INSIGHTS_ACTIVITY_TYPE_KINDS: SalesInsightsActivityTypeKind[] = ['standard', 'custom', 'goal'];

export interface SalesInsightsActivityType {
  code: string;
  name: string;
  kind: SalesInsightsActivityTypeKind;
  goalId?: string | null;
  goalTitle?: string | null;
  countsAsVisit: boolean;
  color?: number | null;
  icon?: string | null;
  active: boolean;
  configured?: boolean;
}

export interface SalesInsightsActivityTypeRequest {
  name?: string | null;
  countsAsVisit?: boolean | null;
  color?: number | null;
  icon?: string | null;
  active?: boolean | null;
}

export type SalesInsightsGoalDoneSource = 'activity' | 'manual';

export interface SalesInsightsGoalCustomer extends SalesInsightsCustomerHealthRow {
  done: boolean;
  doneBy?: string | null;
  doneAt?: string | null;
  note?: string | null;
  doneSource?: SalesInsightsGoalDoneSource | null;
  activityId?: string | null;
  activityDate?: string | null;
  manual?: boolean | null;
}

export interface SalesInsightsGoalCustomersResponse {
  goal: SalesInsightsGoal;
  computedAt: number;
  rows: SalesInsightsGoalCustomer[];
}

export interface SalesInsightsGoalCheck {
  customerId: string;
  done?: boolean;
  note?: string | null;
  reset?: boolean;
}

export interface SalesInsightsGoalOptions {
  activityActions: SalesInsightsFilterOption[];
  dimensions?: string[];
  flags?: string[];
}

export const SALES_INSIGHTS_GOAL_EXTRA_KEY = 'salesInsightsGoalId';

export type SalesInsightsDataScope = 'activities' | 'goals' | 'activityTypes';
